import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { ModalRoute } from "../routing/ModalRoute";
import Card from "../ui/Card";
import { useMailboxScope } from "../../contexts/MailboxScopeContext";
import { leadsService } from "../../services/leads";

function formatRunDate(iso) {
  if (iso == null || iso === "") return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function humanizeStatus(status) {
  if (!status) return "—";
  return status
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function PastAuditsContent() {
  const navigate = useNavigate();
  const mailboxScope = useMailboxScope();

  const tasksQuery = useQuery({
    queryKey: ["bulk-email-tasks", mailboxScope?.mailboxHash],
    queryFn: () => leadsService.listBulkEmailTasks(),
    enabled: Boolean(mailboxScope?.mailboxHash) && !mailboxScope?.mailboxesLoading,
  });

  const rows =
    (tasksQuery.data || []).filter((t) => t.mailbox_hash === mailboxScope?.mailboxHash) || [];

  return (
    <Card className="max-w-2xl w-full max-h-[min(85vh,40rem)] flex flex-col p-6 md:p-8 relative">
      <button
        type="button"
        className="absolute top-4 right-4 text-theme-dim hover:text-theme-text font-mono text-xs z-10"
        onClick={() => navigate("/audit/")}
      >
        Close
      </button>
      <h3 className="font-display font-bold italic text-xl text-theme-on-background mb-1 pr-10">Past audits</h3>
      <p className="text-theme-muted text-sm mb-4">
        Bulk import runs for{" "}
        <span className="text-theme-text">
          {mailboxScope?.mailboxes?.find((m) => m.hash === mailboxScope?.mailboxHash)?.email || "this mailbox"}
        </span>
        . Requested count is how far back we asked the provider to look; examined is how many messages we pulled in
        this run.
      </p>
      {!mailboxScope?.mailboxHash ? (
        <p className="text-theme-muted text-sm">Select a mailbox in the sidebar to see runs.</p>
      ) : tasksQuery.isLoading ? (
        <p className="font-mono text-[11px] text-theme-dim">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-theme-muted text-sm">
          No audits yet for this mailbox. Start a new inbox audit to create one.
        </p>
      ) : (
        <div className="overflow-auto rounded-lg border border-theme-border -mx-1">
          <table className="w-full text-left font-mono text-[11px]">
            <thead className="bg-theme-panel/80 border-b border-theme-border sticky top-0">
              <tr>
                <th className="px-3 py-2 text-theme-dim uppercase tracking-[0.08em]">Started</th>
                <th className="px-3 py-2 text-theme-dim uppercase tracking-[0.08em]">Status</th>
                <th className="px-3 py-2 text-theme-dim uppercase tracking-[0.08em] text-right">Requested</th>
                <th className="px-3 py-2 text-theme-dim uppercase tracking-[0.08em] text-right">Examined</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.hash} className="border-b border-theme-border last:border-b-0">
                  <td className="px-3 py-2.5 text-theme-text whitespace-nowrap">{formatRunDate(row.create_date)}</td>
                  <td className="px-3 py-2.5 text-theme-muted">{humanizeStatus(row.status)}</td>
                  <td className="px-3 py-2.5 text-theme-dim tabular-nums text-right">{row.requested_count ?? "—"}</td>
                  <td className="px-3 py-2.5 text-theme-dim tabular-nums text-right">{row.found_count ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows.some((r) => r.last_error) ? (
        <p className="mt-3 text-theme-muted text-xs">
          Some runs recorded an error. Retry a new audit if results look incomplete.
        </p>
      ) : null}
    </Card>
  );
}

export default function PastAuditsModal() {
  return (
    <ModalRoute closeTo="/audit/" dialogClassName="w-full max-w-2xl">
      <PastAuditsContent />
    </ModalRoute>
  );
}
